// R23 — THE HOME PAGE AND THE FALLTHROUGH, AND WHY THESE TWO CAME OUT TOGETHER.
//
// They are the two ends of one decision. `HomePage` is where a member is offered a room; the
// router's twenty-arm ternary decides which arm she lands on; and `GeneratorPage` is what catches
// her when NO arm did. Neither carries markup worth moving. What they carry is a routing decision
// that is silently wrong or silently right depending on the ORDER of a ternary nobody can test:
//
//   • HomePage decides which cards exist and where each one sends her — `homeMenuItems` for the
//     grid, and the two "Additional rooms" links, which are a hand-written filter over `generators`.
//   • GeneratorPage is THE ONLY SURFACE ON THIS SITE that tells a member a room is coming soon, and
//     WHICH rooms reach it is stated nowhere: it is whatever the router failed to branch on. Delete
//     an arm, reorder one below the fallthrough, or ship a room and forget its arm, and a built room
//     she has paid for renders "This room is coming soon." Nothing throws and nothing logs.
//
// Both are hookless, so `scripts/r23-page-router.mjs` CALLS them and reads the tree back (R22's
// argument): every button's target page is checked against the real roster, and the coming-soon copy
// is checked on the thing that actually renders it rather than on a string in a 14,000-line file.
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { generators, homeMenuItems, type Generator } from "../catalogs/rooms";
import type { Page } from "../lib/pages";
// HOME-1 FINAL: the copy is a DECLARATION rather than a literal in the markup, so a gate can read it
// and ITEM 14b's census can see it — a headline typed into a component is one nothing can check.
import { HOME_HERO_LINE, HOME_SUB_LINE } from "../homeTapMap.js";
// LANDING v3 (owner, 2026-08-22, live under her 2026-08-23 priority law): her new ad target.
// HOME-1's artwork and its tap map are KEPT rather than deleted -- both files are still hers, both
// maps still hold, and `--check` would go RED about a roster entry whose source is gone -- but what
// a stranger sees is this.
import { LandingArtwork } from "./LandingArtwork";
import { HomeArt } from "./HomeArt";
import { HOME_ROOMS_ANCHOR } from "../homeArt.js";

// The two rooms her home page links to beneath the card grid. A hand-written filter over
// `generators`, kept as a named list so the gate can hold both targets to the roster.
export const ADDITIONAL_ROOM_IDS: Page[] = ["human-design", "daily"];

// ---------------------------------------------------------------------------------------------
// HOME-1 — **THE SIGNED-OUT HOME IS THE FLOATING QUESTION FIELD.**
//
// Her paid advertising lands here and *"must be seized here"* (clause 5), so a stranger meets her
// hero line and her questions over the art rather than a grid of room cards.
//
// >>> **A SIGNED-IN MEMBER'S HOME IS UNTOUCHED**, which is her clause 1's own scope (*"this is the
// stranger's first sight only"*) and is also what makes this safe to ship fast while ads are
// running: nobody who already pays sees a changed page, so the only surface at risk is the one that
// is currently costing her money. <<<
// ---------------------------------------------------------------------------------------------
export function StrangerHome({ goTo, onMenu, onLogin, onSignup, onFunnel, onTalk }: {
  goTo: (page: Page) => void;
  onMenu?: () => void;
  onLogin?: () => void;
  // LANDING v3: her three ad doors carry a KEY, which a Page cannot -- `/funnel` with no `?f=`
  // resolves F1's FALLBACK, so routing these as pages files every paid click under one
  // advertisement with nothing red anywhere (see `LandingArtwork`'s own note).
  onFunnel?: (key: string) => void;
  // Her 2026-08-23 order: the Ask Lala box goes to `/talk`, which is F4-1's own door. It is a
  // separate carrier from `goTo` because the talk door decides between signup and the conversation
  // itself (`talkStartPlan`), which a bare page navigation cannot.
  onTalk?: () => void;
  // LANDING v3: what rides with her is a TAG rather than HOME-1's reading key -- her clause (f)'s
  // relationship-interest, kept for the day a relationship funnel exists.
  onSignup?: (tag?: string) => void;
}) {
  // HER CLAUSE 1 (unchanged from the first render): the menu collapses, and the class rides a BODY
  // class because the sidebar mounts outside this page's tree. It lives HERE because it belongs to
  // the surface that needs it: mounted means collapsed, unmounted means restored, by construction.
  useEffect(() => {
    // HER RULING 2026-08-26 SUPERSEDES CLAUSE 1 AT DESKTOP WIDTH: the menu is REVEALED on desktop
    // here. The second class is what tells the two surfaces apart -- the membership frame still
    // wants no menu at all, so it keeps `is-stranger-home` alone (her item 4).
    document.body.classList.add("is-stranger-home", "is-home-menu");
    return () => document.body.classList.remove("is-stranger-home", "is-home-menu");
  }, []);
  return (
    <section className="stranger-home stranger-home--art" aria-label="12th House">
      {/* >>> HER DESIGN, SERVED WHOLE. <<< The hero, her copy, both buttons, the category line and
          the six reading cards are all painted INTO her artwork, so nothing is rebuilt here as type
          (the mockup-matching law). The page's accessible name and its <h1> live in the zones and
          the heading below, because pixels say nothing to a screen reader. */}
      <h1 className="visually-hidden">{HOME_HERO_LINE}</h1>
      <p className="visually-hidden">{HOME_SUB_LINE}</p>
      {/* >>> LANDING v3 -- HER AD TARGET, AND THE ADS ARE RUNNING. <<< Her two masters replace
          HOME-1's on this one surface. Nothing signed-in changes: this branch is the stranger's
          first sight only, which is what makes it safe to ship while money is being spent on it.

          **HER MENU LAW STANDS AND NEEDS NO ARGUMENT HERE:** neither new master paints a menu, so
          HOME-1's own `artworkDrawsMenu` law would leave the house's collapse affordance in place
          -- and this page does not call it, so the affordance stays, which is that same answer
          arrived at by construction rather than by a flag.

          The two visually-hidden lines above are HOME-1's and are KEPT: they are this page's
          accessible NAME rather than a claim about the artwork, both are still true of her landing,
          and every word of her landing's own copy is painted INTO the composition. */}
      <LandingArtwork
        onFunnel={onFunnel || (() => {})}
        onTalk={onTalk || (() => {})}
        onLogin={onLogin || (() => {})}
        onSignup={onSignup || (() => (onLogin || (() => {}))())}
      />
    </section>
  );
}

export function HomePage({ goTo, showcase, signedIn = true }: {
  // The showcase holds state, so it is composed IN rather than imported: a component in here cannot
  // import back out of App.tsx (R12's ruling), and threading it as a node costs nothing and keeps
  // this page callable, which is the entire point of it being out here.
  showcase: ReactNode;
  goTo: (page: Page) => void;
  signedIn?: boolean;
  onMenu?: () => void;
  onLogin?: () => void;
  // LANDING v3: her landing hands a TAG where HOME-1's map handed the reading key of the card
  // tapped. This page passes every carrier straight through and decides none of them.
  onSignup?: (tag?: string) => void;
  onFunnel?: (key: string) => void;
  onTalk?: () => void;
}) {
  // >>> ITEM HOME-GO (her go word, 2026-10-01): HER HOME ART IS THE FRONT DOOR FOR EVERYONE. <<<
  // Signed out and signed in alike meet her art with its tap areas (`HomeArt`); the room grid
  // beneath it is the house's rooms index her drawn "Explore the Rooms" scrolls to
  // (EXPLORE-ROOMS-Q1). `StrangerHome` (the ad-era landing; her ads land on /funnel, not here) and
  // the member showcase are no longer rendered here; both are kept, unrendered, so her answer can
  // bring either back in a line. The props stay so the shell's wiring is unchanged.
  // >>> HER CORRECTION, 2026-10-02: HER ART IS THE SIGNED-OUT FRONT DOOR ONLY. <<< Her words: "when you
  // sign in with the membership, the homeback.png page should still be there." A signed-in member gets
  // the members' home exactly as before HOME-GO: the member showcase over the house's own homeback
  // ground. Her new art is for the visitor with no account.
  return (
    <>
      {signedIn ? showcase : (
        <section className="home-go" aria-label="12th House">
          <h1 className="visually-hidden">12th House. Ancient Wisdom for a Modern Life.</h1>
          <HomeArt goTo={goTo} />
        </section>
      )}

      <section className="section tools-menu" id={HOME_ROOMS_ANCHOR}>
        <div className="tools-menu__header">
          <h2>Choose a Room to Explore. Create. Decode.</h2>
          <span aria-hidden="true" />
        </div>

        <div className="tool-card-grid">
          {homeMenuItems.map((item) => (
            <button className="tool-card" key={item.title} type="button" onClick={() => goTo(item.page)}>
              <span className="tool-card__icon">{item.icon}</span>
              <span className="tool-card__copy">
                <span className="tool-card__title-row">
                  <strong>{item.title}</strong>
                  {item.badge ? <em>{item.badge}</em> : null}
                </span>
                <small>{item.description}</small>
              </span>
              <ArrowRight className="tool-card__arrow" size={26} />
            </button>
          ))}
        </div>
      </section>

      {/* HER WORD, 2026-10-02: the two stray buttons under the rooms list ("There's no reason why those two buttons should be there") are removed; the rooms stay reachable from the grid and the menu. */}
    </>
  );
}

// THE COMING-SOON COPY, and it states the law that R23 found the routing map breaking. It refuses a
// subscribe-to-unlock promise BY NAME — and `PAGE_FEATURE` was pricing the one page that reaches
// here, so a member below tier was shown the purple box first and this sentence second. A door that
// costs money may not open onto this page; `COMING_SOON_PAGES` in src/lib/pages.ts is that law and
// scripts/r23-page-router.mjs runs it.
export const COMING_SOON_NOTE = "This room is coming soon. Your membership already includes it the moment it opens.";

export function GeneratorPage({ generator, goHome }: { generator: Generator; goHome: () => void }) {
  return (
    <section className={`page-shell library-page generator-page generator-page--${generator.id}`}>
      {generator.background ? (
        <div
          className="generator-backdrop"
          aria-hidden="true"
          style={{ backgroundImage: `url("${generator.background}")` }}
        />
      ) : null}
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        Back to home
      </button>

      <div className="simple-room">
        <span className="simple-room__icon">{generator.icon}</span>
        <p className="eyebrow">{generator.detail}</p>
        <h1>{generator.pageTitle || generator.title}</h1>
        <p>{generator.description}</p>
        {/* These rooms have no reading engine yet, so the copy is an honest "coming
            soon" rather than a subscribe-to-unlock promise (never a dead button). */}
        <div className="locked-note locked-note--soon">
          <Sparkles size={22} />
          <span>{COMING_SOON_NOTE}</span>
        </div>
      </div>
    </section>
  );
}
